import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { uploadNashCert } from '../api/client.js';
import { StepPaper } from './StepPaper.js';
import type { WizardStepProps } from './WizardRouter.js';
import { colors } from '@scriptpad/shared';

export const Step4OrgUpload = ({ guid, data, onAdvance, onPatched }: WizardStepProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [pic, setPic] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      if (accepted[0]) setFile(accepted[0]);
    },
    accept: {
      'application/x-pkcs12': ['.p12'],
      'application/octet-stream': ['.p12'],
    },
    maxFiles: 1,
    maxSize: 4 * 1024 * 1024,
  });

  const mutation = useMutation({
    mutationFn: () => uploadNashCert(guid, file!, pic),
    onSuccess: (next) => {
      // Successful verification - persist + jump straight to the next step.
      // No interstitial "certificate verified" panel; the stepper marks this
      // step as completed which is signal enough.
      onPatched(next);
      onAdvance(5);
    },
  });

  const cert = data.nashCert;
  const hasNewUpload = file !== null && pic.length > 0;

  // Primary action is always labelled "Continue":
  //   - new file picked → upload+verify, then auto-advance
  //   - cert already on record (resume case) → just advance
  //   - nothing yet → disabled
  const onContinue = (): void => {
    if (hasNewUpload) mutation.mutate();
    else if (cert) onAdvance(5);
  };
  const continueDisabled = mutation.isPending || (!hasNewUpload && !cert);

  return (
    <StepPaper>
      <Stack spacing={3}>
        <Typography variant="h3">Upload your NASH PKI certificate</Typography>
        <Typography variant="body1" color="text.secondary">
          Please upload the certificate file (<code>site.p12</code>) below, and enter the PIC (password)
          you have received via SMS.
        </Typography>

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? colors.primary : 'divider',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            bgcolor: isDragActive ? colors.bgWash : 'transparent',
            cursor: 'pointer',
            transition: 'background-color 150ms ease',
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 40, color: colors.primary, mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {file ? file.name : 'Drag and drop your site.p12 here'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {file ? `${(file.size / 1024).toFixed(1)} KB` : 'or click to browse'}
          </Typography>
        </Box>

        <TextField
          label="PIC (Password)"
          value={pic}
          onChange={(e) => setPic(e.target.value)}
          autoComplete="off"
          // Plain text on purpose - the PIC arrives via SMS and users routinely
          // mistype it; making the field visible avoids a frustrating retype
          // loop. Treat the value as a one-shot secret rather than a stored
          // password.
          inputProps={{ autoCapitalize: 'off', spellCheck: false }}
        />

        {mutation.isError && (
          <Alert severity="error">{(mutation.error as Error).message ?? 'Failed to verify certificate.'}</Alert>
        )}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Button onClick={() => onAdvance(3)} variant="text">
            Back
          </Button>
          <Button
            size="large"
            variant="contained"
            disabled={continueDisabled}
            onClick={onContinue}
            startIcon={mutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    </StepPaper>
  );
};
